import {
    Mesh,
    PlaneGeometry,
    ShaderMaterial
} from "three";

export default class BenMarcHeroBackground {
    constructor(experience) {
        this.experience = experience;
        this.init();
    }

    init() {
        this.createShader();
        this.createGeometry();
        this.createMaterial();
        this.createMesh();
    }

    createShader() {
        this.vertexShader = `
                varying vec2 vUv;
                void main() {
                    vUv = uv;
                    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
                }
            `;

        this.fragmentShader = `
                varying vec2 vUv;

vec3 mod289(vec3 x) {
    return x - floor(x * (1.0 / 289.0)) * 289.0;
}

vec4 mod289(vec4 x) {
    return x - floor(x * (1.0 / 289.0)) * 289.0;
}

vec4 permute(vec4 x) {
    return mod289(((x * 34.0) + 1.0) * x);
}

vec4 taylorInvSqrt(vec4 r) {
    return 1.79284291400159 - 0.85373472095314 * r;
}

float snoise(vec3 v) {
    const vec2 C = vec2(1.0 / 6.0, 1.0 / 3.0);
    const vec4 D = vec4(0.0, 0.5, 1.0, 2.0);
    
    vec3 i = floor(v + dot(v, C.yyy));
    vec3 x0 = v - i + dot(i, C.xxx);
    
    vec3 g = step(x0.yzx, x0.xyz);
    vec3 l = 1.0 - g;
    vec3 i1 = min(g.xyz, l.zxy);
    vec3 i2 = max(g.xyz, l.zxy);
    
    vec3 x1 = x0 - i1 + C.xxx;
    vec3 x2 = x0 - i2 + C.yyy;
    vec3 x3 = x0 - D.yyy;
    
    i = mod289(i);
    vec4 p = permute(permute(permute(
                i.z + vec4(0.0, i1.z, i2.z, 1.0))
                + i.y + vec4(0.0, i1.y, i2.y, 1.0))
                + i.x + vec4(0.0, i1.x, i2.x, 1.0));
                
    float n_ = 0.142857142857;
    vec3 ns = n_ * D.wyz - D.xzx;
    
    vec4 j = p - 49.0 * floor(p * ns.z * ns.z);
    
    vec4 x_ = floor(j * ns.z);
    vec4 y_ = floor(j - 7.0 * x_);
    
    vec4 x = x_ * ns.x + ns.yyyy;
    vec4 y = y_ * ns.x + ns.yyyy;
    vec4 h = 1.0 - abs(x) - abs(y);
    
    vec4 b0 = vec4(x.xy, y.xy);
    vec4 b1 = vec4(x.zw, y.zw);
    
    vec4 s0 = floor(b0) * 2.0 + 1.0;
    vec4 s1 = floor(b1) * 2.0 + 1.0;
    vec4 sh = -step(h, vec4(0.0));
    
    vec4 a0 = b0.xzyw + s0.xzyw * sh.xxyy;
    vec4 a1 = b1.xzyw + s1.xzyw * sh.zzww;
    
    vec3 p0 = vec3(a0.xy, h.x);
    vec3 p1 = vec3(a0.zw, h.y);
    vec3 p2 = vec3(a1.xy, h.z);
    vec3 p3 = vec3(a1.zw, h.w);
    
    vec4 norm = taylorInvSqrt(vec4(dot(p0, p0), dot(p1, p1), dot(p2, p2), dot(p3, p3)));
    p0 *= norm.x;
    p1 *= norm.y;
    p2 *= norm.z;
    p3 *= norm.w;
    
    vec4 m = max(0.6 - vec4(dot(x0, x0), dot(x1, x1), dot(x2, x2), dot(x3, x3)), 0.0);
    m = m * m;
    return 42.0 * dot(m * m, vec4(dot(p0, x0), dot(p1, x1),
                                dot(p2, x2), dot(p3, x3)));
}

float fbm(vec3 p) {
    float value = 0.0;
    float amplitude = 1.0;
    float frequency = 0.025;
    float lacunarity = 3.0;
    float persistance = 0.6;
    float scale = 0.8;
    int octaves = 3;
    
    for (int i = 0; i < octaves; i++) {
        float noiseVal = snoise(p * frequency * scale);
        
        value += amplitude * noiseVal;
        frequency *= lacunarity;
        amplitude *= persistance;
    }
    
    return value;
}

float paperNoise(vec2 uv) {
    float n1 = fbm(vec3(uv.xy + vec2(1.0, 0.0), 0.0));
    float n2 = fbm(vec3(uv.xy - vec2(1.0, 0.0), 0.0));
    float n = n1 - n2;
    return n;
}

void main() {
    vec3 color1 = vec3(0.043, 0.314, 0.365); // #0B505D
    vec3 color2 = vec3(0.0, 0.0, 0.0);       // #000000

    // Compute the gradient color
    vec3 gradientColor = mix(color1, color2, smoothstep(0.499, 1.0, 1.0 - vUv.y));

    // Apply paper texture noise
    float noise = paperNoise(vUv * 700.0) * 0.02;
    vec3 finalColor = gradientColor + noise;

    gl_FragColor = vec4(finalColor, 1.0);
}

            `;
    }

    createGeometry() {
        this.geometry = new PlaneGeometry(this.experience.sizes.emWidth, this.experience.sizes.emHeight);
        this.originalWidth = this.experience.sizes.emWidth;
        this.originalHeight = this.experience.sizes.emHeight;
    }

    createMaterial() {
        this.material = new ShaderMaterial({
            vertexShader: this.vertexShader,
            fragmentShader: this.fragmentShader
        });
    }

    createMesh() {
        this.mesh = new Mesh(this.geometry, this.material);
    }

    resize() {
        this.mesh.scale.x = this.experience.sizes.emWidth / this.originalWidth;
        this.mesh.scale.y = this.experience.sizes.emHeight / this.originalHeight;
    }
}



